import React from 'react';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { GtmContentField, GtmContentFieldOption } from './gtmContentSpecs';

/**
 * One form control per spec field.
 *
 * The content tables are plain copy — strings, numbers, a couple of enums and one boolean — so
 * this covers every field in `gtmContentSpecs` and keeps the dialog free of per-table markup.
 */

export type GtmOptionSources = {
  vendors: GtmContentFieldOption[];
  comparisonVendors: GtmContentFieldOption[];
};

const EMPTY_OPTIONS: GtmContentFieldOption[] = [];

const optionsFor = (field: GtmContentField, sources: GtmOptionSources): GtmContentFieldOption[] => {
  if (field.options) return field.options;
  if (field.optionsFrom) return sources[field.optionsFrom] ?? EMPTY_OPTIONS;
  return EMPTY_OPTIONS;
};

/** The API sends FKs as an id or as a nested object depending on depth; a select needs the id. */
const toSelectValue = (value: any): string | number => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return value.id ?? '';
  return value;
};

export const GtmContentFieldInput: React.FC<{
  field: GtmContentField;
  value: any;
  onChange: (value: any) => void;
  disabled?: boolean;
  optionSources: GtmOptionSources;
}> = ({ field, value, onChange, disabled = false, optionSources }) => {
  if (field.type === 'boolean') {
    return (
      <FormControlLabel
        control={
          <Checkbox
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
          />
        }
        label={field.label}
        sx={{ mt: 2 }}
      />
    );
  }

  if (field.type === 'select') {
    const options = optionsFor(field, optionSources);
    const labelId = `gtm-field-${field.key}`;

    return (
      <FormControl fullWidth margin="normal" disabled={disabled}>
        <InputLabel id={labelId}>{field.label}</InputLabel>
        <Select
          labelId={labelId}
          label={field.label}
          value={toSelectValue(value)}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        >
          {!field.required && (
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
          )}
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  if (field.type === 'number') {
    return (
      <TextField
        fullWidth
        margin="normal"
        type="number"
        label={field.label}
        value={value ?? 0}
        onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
        helperText={field.helperText}
        disabled={disabled}
      />
    );
  }

  return (
    <TextField
      fullWidth
      margin="normal"
      label={field.label}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      required={!!field.required}
      multiline={field.type === 'multiline'}
      minRows={field.type === 'multiline' ? field.minRows ?? 2 : undefined}
      helperText={field.helperText}
      disabled={disabled}
    />
  );
};

/** The spec's fields laid out in a two-column grid; `half: true` fields share a row. */
export const GtmContentFieldGrid: React.FC<{
  fields: GtmContentField[];
  form: Record<string, any>;
  onFieldChange: (key: string, value: any) => void;
  disabled?: boolean;
  optionSources: GtmOptionSources;
}> = ({ fields, form, onFieldChange, disabled = false, optionSources }) => (
  <Grid container spacing={2}>
    {fields.map((field) => (
      <Grid item xs={12} sm={field.half || field.type === 'boolean' ? 6 : 12} key={field.key}>
        <GtmContentFieldInput
          field={field}
          value={form[field.key]}
          onChange={(value) => onFieldChange(field.key, value)}
          disabled={disabled}
          optionSources={optionSources}
        />
      </Grid>
    ))}
  </Grid>
);
